<template lang="html">
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-3">
      <USelect
        v-model="sort"
        :items="sortItems"
        value-key="value"
        aria-label="Сортировка отзывов"
        class="w-56"
      />
      <UCheckbox v-model="withPhotos" label="Только с фото" />
    </div>

    <ReviewCard v-for="review in collection" :key="review.id" :review="review" />

    <p v-if="view.empty" class="py-6 text-sm text-muted">
      {{ withPhotos ? 'Отзывов с фото пока нет.' : 'Отзывов пока нет — будьте первым.' }}
    </p>

    <div v-if="view.failed" class="flex flex-col items-center gap-2 py-6 text-sm" role="alert">
      <p class="text-muted">
        Не удалось загрузить отзывы
      </p>
      <UButton
        variant="outline"
        icon="i-lucide-refresh-cw"
        label="Повторить"
        :loading="pending"
        @click="retry()"
      />
    </div>

    <UButton
      v-if="view.more"
      variant="outline"
      label="Показать ещё"
      :loading="pending"
      class="self-center"
      @click="showMore()"
    />
  </div>
</template>

<script setup lang="ts">
import {
  type PageOfShowcaseReviewDto,
  type ReviewsListByProductRequestQuery,
  type ShowcaseReviewDto,
  SortDirection,
} from '@repo/api/web/models'

import ReviewCard from './ReviewCard.vue'
import { useListingLoads } from '../composables/useListingLoads'
import { useReviews } from '../composables/useReviews'
import { listingView } from '../utils/listingView'
import {
  DEFAULT_REVIEWS_SORT,
  parseReviewsSort,
  REVIEWS_FEED_LIMIT,
  REVIEWS_SORT_OPTIONS,
  reviewsSortValue,
} from '../utils/reviewsSort'

import { useCursorListing } from '#Common/composables/useCursorListing'

const props = defineProps<{
  productId: number
  /**
   * Первая страница с запросом по умолчанию (`REVIEWS_FEED_LIMIT`, сначала новые, без фильтра), если её уже
   * загрузил родитель: тогда лента свой первый запрос не делает. Дальше — «Показать ещё»,
   * сортировка и фильтр как обычно.
   */
  initialPage?: PageOfShowcaseReviewDto | null
}>()

const { service } = useReviews()

const sortItems = [...REVIEWS_SORT_OPTIONS]
const defaultSort = parseReviewsSort(DEFAULT_REVIEWS_SORT)

/**
 * Перезагрузить ленту с первой страницы (после нового отзыва): курсор сбрасывается,
 * выбранные сортировка и фильтр сохраняются. Объявлена до `await`: defineExpose после него
 * запрещён; setFilter/load/run ниже к моменту вызова уже инициализированы
 */
function reload() {
  return run(() => {
    setFilter({ CursorField: undefined, CursorId: undefined } satisfies Partial<ReviewsListByProductRequestQuery>)
    return load(true)
  })
}

defineExpose({ reload })

/**
 * Готовая первая страница отдаётся первому вызову fetcher'а (первая загрузка useCursorListing —
 * на SSR или при переходе на клиенте) и сразу забывается. При гидратации fetcher не вызывается,
 * поэтому после `await useCursorListing` она тоже забывается: следующие запросы — только к API
 */
let seededPage = props.initialPage ?? undefined

// Первую страницу useCursorListing грузит на SSR сам; «Показать ещё» — на клиенте.
// TQuery = any, как в остальных листингах витрины: ограничение useCursorListing
// (SortField: keyof TModel, CursorField: string[]) не сходится со сгенерированными query-моделями;
// тип запроса держат аннотация fetcher'а и `satisfies` в фильтре
const { query, collection, pending, error, hasAfter, nextPage, setSort, setFilter, load } = await useCursorListing<
  any,
  ShowcaseReviewDto
>({
  key: `product-reviews-${props.productId}`,
  fetcher: (listQuery: ReviewsListByProductRequestQuery) => {
    const seeded = seededPage
    seededPage = undefined
    return seeded ? Promise.resolve(seeded) : service.listByProduct(props.productId, listQuery)
  },
  initialQuery: {
    Limit: REVIEWS_FEED_LIMIT,
    SortField: defaultSort.field,
    SortDirection: defaultSort.desc ? SortDirection.DESC : SortDirection.ASC,
  },
})
seededPage = undefined

// Сбой первой страницы на SSR — сразу ошибка с повтором, а не «отзывов пока нет»
const { failed, run, retry } = useListingLoads(error.value ? () => load(true) : undefined)

const view = computed(() => listingView({
  pending: pending.value,
  count: collection.value.length,
  failed: failed.value,
  hasAfter: hasAfter.value,
}))

/** После сбоя nextPage() уже ничего не делает — повторяется загрузка с курсором из запроса */
function showMore() {
  return run(() => nextPage(), () => load(false))
}

/** Смена сортировки перезапрашивает ленту с начала (setSort сам сбрасывает курсор) */
const sort = computed({
  get: () => reviewsSortValue(query.value.SortField, query.value.SortDirection),
  set: (value: string) => {
    const { field, desc } = parseReviewsSort(value)
    setSort([{ id: field, desc }])
    run(() => load(true))
  },
})

/** Фильтр «только с фото»: курсор прошлой выдачи сбрасывается, лента грузится заново */
const withPhotos = computed({
  get: () => query.value['Filter.WithPhotos'] === true,
  set: (value: boolean | 'indeterminate') => {
    setFilter({
      'Filter.WithPhotos': value === true || undefined,
      CursorField: undefined,
      CursorId: undefined,
    } satisfies Partial<ReviewsListByProductRequestQuery>)
    run(() => load(true))
  },
})
</script>
